import appConfig from "@/config/app-config.json";
import type {
    AboutConfig,
    AppConfigResponse,
    ContactConfig,
    HomeConfig,
    SeoSettingsConfig,
    ServiceConfig,
    SiteSettingsConfig,
} from "@/services/configService";

const APP_CONFIG_STORAGE_KEY = "app-config";

export const sanitizeAppConfigResponse = (data: unknown): AppConfigResponse => {
    if (!data || typeof data !== "object") {
        return {};
    }

    const response = data as Record<string, unknown>;
    const config: AppConfigResponse = {};

    if (response.about !== undefined) {
        config.about = response.about as AboutConfig;
    }

    if (response.contact !== undefined) {
        config.contact = response.contact as ContactConfig;
    }

    if (response.home !== undefined) {
        config.home = response.home as HomeConfig;
    }

    if (response.services !== undefined) {
        config.services = response.services as ServiceConfig[];
    }

    if (response.site_settings !== undefined) {
        config.site_settings = response.site_settings as SiteSettingsConfig;
    }

    if (response.seo_settings !== undefined) {
        config.seo_settings = response.seo_settings as SeoSettingsConfig;
    }

    return config;
};

export const getFallbackAppConfig = (): AppConfigResponse => {
    return sanitizeAppConfigResponse(appConfig);
};

export const getStoredAppConfig = (): AppConfigResponse | null => {
    if (typeof window === "undefined") {
        return null;
    }

    try {
        const storedConfig = window.localStorage.getItem(APP_CONFIG_STORAGE_KEY);

        if (!storedConfig) {
            return null;
        }

        const parsedConfig = sanitizeAppConfigResponse(JSON.parse(storedConfig));

        return Object.keys(parsedConfig).length > 0 ? parsedConfig : null;
    } catch (error) {
        console.error("Failed to read app config from localStorage.", error);
        return null;
    }
};

export const getInitialAppConfig = (): AppConfigResponse => {
    return getStoredAppConfig() ?? getFallbackAppConfig();
};

export const setStoredAppConfig = (config: AppConfigResponse) => {
    if (typeof window === "undefined") {
        return;
    }

    try {
        window.localStorage.setItem(APP_CONFIG_STORAGE_KEY, JSON.stringify(config));
    } catch (error) {
        console.error("Failed to write app config to localStorage.", error);
    }
};
